/**
 * Schedule utilities for POI hours and seasonal availability.
 * Uses visitor's local browser time (no timezone config needed).
 */

// Re-define types from @wondermaps/db to avoid cross-package rootDir issues
// These must stay in sync with packages/db/src/schema.ts

export interface DaySchedule {
  day: 0 | 1 | 2 | 3 | 4 | 5 | 6; // Sun=0, Sat=6
  open: string; // "09:00"
  close: string; // "17:00"
  closed: boolean;
}

export interface HoursConfig {
  type: "always_open" | "scheduled" | "variable";
  schedule?: DaySchedule[]; // if type === "scheduled"
  note?: string; // "Closed for winter", etc.
}

export interface SeasonDates {
  start: string; // "MM-DD" format
  end: string; // "MM-DD" format
}

/**
 * Checks if current time falls within scheduled hours
 */
export function isCurrentlyOpen(
  hours: HoursConfig | null | undefined,
  now: Date = new Date()
): boolean {
  if (!hours) return false;

  if (hours.type === "always_open") return true;
  if (hours.type === "variable") return false; // Indeterminate

  if (hours.type === "scheduled" && hours.schedule) {
    const currentDay = now.getDay() as DaySchedule["day"];
    const todaySchedule = hours.schedule.find((s) => s.day === currentDay);

    if (!todaySchedule || todaySchedule.closed) {
      return false;
    }

    const currentTime =
      now.getHours().toString().padStart(2, "0") +
      ":" +
      now.getMinutes().toString().padStart(2, "0");

    return currentTime >= todaySchedule.open && currentTime < todaySchedule.close;
  }

  return false;
}

/**
 * Checks if current date falls within seasonal date range.
 * Handles wrap-around (e.g., Nov 15 - Mar 15 for winter seasons).
 *
 * @param seasonDates - { start: "MM-DD", end: "MM-DD" }
 * @param now - Current date (defaults to now)
 * @returns true if current date is in-season
 */
export function isInSeason(
  seasonDates: SeasonDates | null | undefined,
  now: Date = new Date()
): boolean {
  if (!seasonDates) return false;

  const month = (now.getMonth() + 1).toString().padStart(2, "0");
  const day = now.getDate().toString().padStart(2, "0");
  const currentMMDD = `${month}-${day}`;

  const { start, end } = seasonDates;

  // Normal case: start <= end (e.g., May 01 - Oct 31)
  if (start <= end) {
    return currentMMDD >= start && currentMMDD <= end;
  }

  // Wrap-around case: start > end (e.g., Nov 15 - Mar 15)
  // In-season if current >= start OR current <= end
  return currentMMDD >= start || currentMMDD <= end;
}

/**
 * Gets next opening time for scheduled hours
 *
 * @returns Date of next opening, or null if always_open/variable
 */
export function getNextOpenTime(
  hours: HoursConfig | null | undefined,
  now: Date = new Date()
): Date | null {
  if (!hours) return null;
  if (hours.type === "always_open") return null; // Already open
  if (hours.type === "variable") return null; // Unknown

  if (hours.type === "scheduled" && hours.schedule) {
    const currentDay = now.getDay() as DaySchedule["day"];
    const currentTime =
      now.getHours().toString().padStart(2, "0") +
      ":" +
      now.getMinutes().toString().padStart(2, "0");

    // Check today first
    const todaySchedule = hours.schedule.find((s) => s.day === currentDay);
    if (todaySchedule && !todaySchedule.closed && currentTime < todaySchedule.open) {
      const [openHour, openMin] = todaySchedule.open.split(":").map(Number);
      const nextOpen = new Date(now);
      nextOpen.setHours(openHour, openMin, 0, 0);
      return nextOpen;
    }

    // Check next 7 days
    for (let i = 1; i <= 7; i++) {
      const checkDay = ((currentDay + i) % 7) as DaySchedule["day"];
      const daySchedule = hours.schedule.find((s) => s.day === checkDay);

      if (daySchedule && !daySchedule.closed) {
        const [openHour, openMin] = daySchedule.open.split(":").map(Number);
        const nextOpen = new Date(now);
        nextOpen.setDate(nextOpen.getDate() + i);
        nextOpen.setHours(openHour, openMin, 0, 0);
        return nextOpen;
      }
    }
  }

  return null;
}

/**
 * Formats today's hours for display
 */
export function formatTodayHours(
  hours: HoursConfig | null | undefined,
  now: Date = new Date()
): string {
  if (!hours) return "";

  if (hours.type === "always_open") return "Open 24/7";
  if (hours.type === "variable") return "Hours Vary";

  if (hours.type === "scheduled" && hours.schedule) {
    const currentDay = now.getDay() as DaySchedule["day"];
    const todaySchedule = hours.schedule.find((s) => s.day === currentDay);

    if (!todaySchedule || todaySchedule.closed) {
      return "Closed today";
    }

    return `${formatTime(todaySchedule.open)} - ${formatTime(todaySchedule.close)}`;
  }

  return "";
}

/**
 * Formats 24h time string to 12h format
 * e.g., "14:30" -> "2:30 PM"
 */
export function formatTime(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

/**
 * Gets today's schedule entry
 */
export function getTodaySchedule(
  schedule: DaySchedule[],
  now: Date = new Date()
): DaySchedule | undefined {
  const today = now.getDay() as DaySchedule["day"];
  return schedule.find((s) => s.day === today);
}

/**
 * Open/closed status result type
 */
export type OpenStatus =
  | { type: "open_24_7" }
  | { type: "open_now"; closesAt?: string }
  | { type: "closed"; opensAt?: string }
  | { type: "hours_vary" }
  | { type: "temp_closed" }
  | { type: "closed_for_season" }
  | { type: "unknown" };

/**
 * Computes the display status for a POI based on status, hours, and season dates.
 * This is the main function for determining what to show visitors.
 */
export function computeOpenStatus(
  status: "normal" | "temp_closed" | "seasonal",
  hours: HoursConfig | null | undefined,
  seasonDates: SeasonDates | null | undefined,
  now: Date = new Date()
): OpenStatus {
  // Temporarily closed always shows regardless of hours
  if (status === "temp_closed") {
    return { type: "temp_closed" };
  }

  // Seasonal: check if in-season first
  if (status === "seasonal") {
    if (!isInSeason(seasonDates, now)) {
      return { type: "closed_for_season" };
    }
    // In-season: fall through to check hours
  }

  // Normal or in-season seasonal: derive from hours
  if (!hours) {
    return { type: "unknown" };
  }

  if (hours.type === "always_open") {
    return { type: "open_24_7" };
  }

  if (hours.type === "variable") {
    return { type: "hours_vary" };
  }

  if (hours.type === "scheduled" && hours.schedule) {
    const open = isCurrentlyOpen(hours, now);
    if (open) {
      const todaySchedule = getTodaySchedule(hours.schedule, now);
      return {
        type: "open_now",
        closesAt: todaySchedule ? formatTime(todaySchedule.close) : undefined,
      };
    } else {
      const nextOpen = getNextOpenTime(hours, now);
      return {
        type: "closed",
        opensAt: nextOpen
          ? formatTime(
              nextOpen.getHours().toString().padStart(2, "0") +
                ":" +
                nextOpen.getMinutes().toString().padStart(2, "0")
            )
          : undefined,
      };
    }
  }

  return { type: "unknown" };
}
